// ════════════════════════════════════════════════════════════════════════
//  Stammdaten eines Lehrgangs
//
//  Nicht Teil des Bundles und damit nicht Teil einer Fassung: Titel, Untertitel,
//  Reihenfolge, Vorgänger und Stilllegung gelten für den Lehrgang als solchen und
//  gelten sofort. Sie sind auch dann änderbar, wenn kein Entwurf offen ist –
//  anders als alles andere auf der Arbeitsfläche.
//
//  Der Titel steht zusätzlich im Bundle, weil die Lern-App eine Fassung für sich
//  allein lesen können muss. Führend ist der hier; ist gerade ein Entwurf offen,
//  wird er dort mitgezogen. Veröffentlichte Fassungen behalten ihren – sie sind
//  unveränderlich, und das ist keine Ausnahme, sondern der Sinn der Sache.
// ════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@otto/plattform";
import { useDraft } from "../../../shared/context/DraftContext.js";
import { COURSES, useCourseSlug } from "../../../shared/lib/coursePath.js";
import {
  curriculumBySlug,
  useCurricula,
  useDeleteCurriculum,
  useUpdateCurriculum,
} from "../lib/data.js";
import styles from "../../../shared/components/shell.module.css";
import own from "../../settings/components/settings.module.css";

export function CourseSettings() {
  const slug = useCourseSlug();
  const navigate = useNavigate();
  const { client } = useSession();
  const curricula = useCurricula(client);
  const update = useUpdateCurriculum(client);
  const remove = useDeleteCurriculum(client);
  const { bundle, setBundle, draft } = useDraft();

  const curriculum = curriculumBySlug(curricula.data, slug);
  const [asking, setAsking] = useState(false);

  if (!curriculum) return <p className={styles.empty}>Wird geladen …</p>;

  const others = (curricula.data ?? []).filter((c) => c.id !== curriculum.id);
  const neverPublished = curriculum.publishedVersion === null;

  const setTitle = (title: string) => {
    update.mutate({ id: curriculum.id, title });
    // Der offene Entwurf zieht mit. Ohne das hieße der Lehrgang in der Liste
    // schon so und in der Fassung, die als Nächstes ausgeliefert wird, noch
    // anders.
    if (draft && bundle) setBundle({ ...bundle, title });
  };

  return (
    <div className={styles.card}>
      <h1 className={styles.title}>Stammdaten</h1>
      <p className={styles.subtitle}>
        Gilt für den Lehrgang, nicht für eine Fassung – Änderungen wirken sofort und
        brauchen keinen Entwurf.
      </p>

      <div className={`${own.pair} ${own.limit}`}>
        <label>
          Titel
          <input
            className={styles.field}
            defaultValue={curriculum.title}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== "" && value !== curriculum.title) setTitle(value);
            }}
          />
        </label>
        <label>
          Kürzel
          <input className={`${styles.field} mono`} value={curriculum.slug} readOnly />
          <span className={own.note}>
            {neverPublished
              ? "Solange nichts veröffentlicht ist, ließe es sich noch ändern – das geht beim Anlegen."
              : "Festgelegt: es steht im Pfad der veröffentlichten Fassungen und im Zwischenspeicher jedes iPads."}
          </span>
        </label>
      </div>

      <div className={styles.spaced}>
        <label htmlFor="settings-subtitle">Untertitel</label>
        <input
          id="settings-subtitle"
          className={styles.field}
          defaultValue={curriculum.subtitle ?? ""}
          onBlur={(e) =>
            update.mutate({
              id: curriculum.id,
              subtitle: e.target.value.trim() === "" ? null : e.target.value.trim(),
            })
          }
        />
        <p className={own.note}>
          Hier steht die Klassenstufe. Die Kinder sehen ihn in der Auswahl, wenn ihre
          Klasse mehrere Lehrgänge führt.
        </p>
      </div>

      <div className={styles.spaced}>
        <label htmlFor="settings-requires">Baut auf</label>
        <select
          id="settings-requires"
          className={styles.field}
          value={curriculum.requiresId ?? ""}
          onChange={(e) =>
            update.mutate({
              id: curriculum.id,
              requiresId: e.target.value === "" ? null : e.target.value,
            })
          }
        >
          <option value="">– steht für sich –</option>
          {others.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <p className={own.note}>
          Eine Klasse kann diesen Lehrgang erst führen, wenn sie den genannten führt.
          Geprüft wird beim Zuordnen durch die Lehrkraft – kein Kind bekommt ein Schloss
          zu sehen.
        </p>
      </div>

      <div className={styles.spaced}>
        <label htmlFor="settings-position">Reihenfolge</label>
        <input
          id="settings-position"
          className={styles.field}
          type="number"
          min={0}
          defaultValue={curriculum.position}
          onBlur={(e) =>
            update.mutate({ id: curriculum.id, position: Number(e.target.value) })
          }
        />
        <p className={own.note}>
          Nur die Sortierung in Listen. Kleiner steht weiter oben.
        </p>
      </div>

      <div className={styles.spaced}>
        <h2 className={own.section}>
          {curriculum.retiredAt ? "Stillgelegt" : "Im Angebot"}
        </h2>
        <p className={own.note}>
          Ein stillgelegter Lehrgang lässt sich keiner Klasse mehr NEU zuordnen. Klassen,
          die ihn führen, üben unverändert weiter – deshalb wird er stillgelegt und nicht
          gelöscht.
        </p>
        <button
          type="button"
          className={styles.buttonQuiet}
          onClick={() =>
            update.mutate({
              id: curriculum.id,
              retiredAt: curriculum.retiredAt ? null : new Date().toISOString(),
            })
          }
        >
          {curriculum.retiredAt ? "Wieder ins Angebot nehmen" : "Stilllegen"}
        </button>
      </div>

      {/* Löschen nur, solange nie veröffentlicht wurde. Danach hängen Klassen und
          Fortschritt daran, und die Datenbank ließe es ohnehin nicht zu. */}
      {neverPublished && (
        <div className={styles.spaced}>
          <h2 className={own.section}>Löschen</h2>
          {asking ? (
            <div className={styles.row}>
              <span>Diesen Lehrgang samt Entwurf löschen?</span>
              <button
                type="button"
                className={styles.button}
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(curriculum.id, { onSuccess: () => navigate(COURSES) })
                }
              >
                Ja, löschen
              </button>
              <button
                type="button"
                className={styles.buttonQuiet}
                onClick={() => setAsking(false)}
              >
                Abbrechen
              </button>
            </div>
          ) : (
            <>
              <p className={own.note}>
                Möglich, solange nichts veröffentlicht ist. Danach bleibt nur das
                Stilllegen.
              </p>
              <button
                type="button"
                className={styles.buttonQuiet}
                onClick={() => setAsking(true)}
              >
                Lehrgang löschen
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
